// Mirrors backend ad_name_constants.py + ad_name_service.py: v2 is positional, legacy names keep tr_ prefixes.

export const SOURCE_REQUEST = "request";
export const SOURCE_AGENCY = "agency"; // agency fills at delivery
export const SOURCE_AUTO = "auto"; // filled by the system
export const SOURCE_HARDCODED = "hardcoded";

// v2: [fieldKey, source, placeholder] — token order is the spec, every token always present.
export const AD_NAME_FIELDS = [
  ["track", SOURCE_REQUEST, "track"],
  ["track_number", SOURCE_REQUEST, "tracknumber"],
  ["topic", SOURCE_REQUEST, "topic"],
  ["script_name", SOURCE_REQUEST, "scriptname"],
  ["module_type", SOURCE_REQUEST, "moduletype"],
  ["module", SOURCE_REQUEST, "module"],
  ["creator_name", SOURCE_AGENCY, "creatorname"],
  ["creative_format", SOURCE_REQUEST, "creativeformat"],
  ["visual_format", SOURCE_REQUEST, "visualformat"],
  ["human_ai", SOURCE_REQUEST, "humanai"],
  ["delivery_date", SOURCE_AUTO, "deliverydate"],
  ["agency_name", SOURCE_REQUEST, "agencyname"],
  ["editor_name", SOURCE_AGENCY, "editorname"],
  ["script_type", SOURCE_REQUEST, "scripttype"],
  ["requester_name", SOURCE_REQUEST, "requester"],
];
export const AD_NAME_TERMINATOR = "ext";

// Legacy: [prefix, fieldKey, source, placeholder]. Only used to recognise old names.
export const AD_NAME_SEGMENTS = [
  ["tr", "track", SOURCE_REQUEST, "track"],
  ["trn", "track_number", SOURCE_REQUEST, "tracknumber"],
  ["an", "app_name", SOURCE_REQUEST, "appname"],
  ["to", "topic", SOURCE_REQUEST, "topic"],
  ["cn", "creator_name", SOURCE_AGENCY, "creatorname"],
  ["fo", "format_required", SOURCE_REQUEST, "format"],
  ["vt", "visual_type", SOURCE_AGENCY, "visualtype"],
  ["at", "ad_type", SOURCE_AGENCY, "adtype"],
  ["dt", "delivery_date", SOURCE_AUTO, "deliverydate"],
  ["agn", "agency_name", SOURCE_REQUEST, "agencyname"],
  ["mo", "module", SOURCE_REQUEST, "module"],
  ["re", "requester_name", SOURCE_REQUEST, "requester"],
  ["ext", null, SOURCE_HARDCODED, ""],
];

export function sanitizeToken(value) {
  if (value == null) return "";
  return String(value).trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function isLegacyName(name) {
  return String(name || "").split("_", 1)[0] === "tr";
}

export function normalizeLegacyAdName(value) {
  if (!value) return "";
  return String(value).replace(/_ext_ext_hook_hook[a-z0-9]*$/, "_ext");
}

// x-join the chosen modules in the configured order for that module type.
export function composeModule(chosen = [], orderedValues = []) {
  const picked = new Set((chosen || []).map(sanitizeToken).filter(Boolean));
  const order = (orderedValues || []).map(sanitizeToken);
  const inOrder = order.filter((m) => picked.has(m));
  const extra = [...picked].filter((m) => !order.includes(m));
  return [...inOrder, ...extra].join("x");
}

function token(key, placeholder, fields) {
  const raw = fields[key];
  if (key === "track_number") return raw == null || raw === "" ? placeholder : "track" + sanitizeToken(raw);
  return sanitizeToken(raw) || placeholder;
}

// The ad set name at request time; agency and system slots hold placeholders.
export function buildRequestRemarks(fields = {}) {
  const parts = AD_NAME_FIELDS.map(([key, , placeholder]) => token(key, placeholder, fields));
  parts.push(AD_NAME_TERMINATOR);
  return parts.join("_");
}

// Split a v2 name into [{key, value, source, isPlaceholder}] for the preview.
export function explainName(name) {
  const tokens = String(name || "").split("_");
  return AD_NAME_FIELDS.map(([key, source, placeholder], i) => {
    const value = tokens[i] ?? "";
    return { key, source, value, isPlaceholder: value === placeholder || value === "" };
  });
}

// Fill agency slots (creator/editor, legacy ad type) on an ad set name and append _hookN.
export function buildDeliveryAdName(adsetName, { creator_name, editor_name, ad_type } = {}, hookNumber) {
  let base = String(adsetName || "");
  if (base && isLegacyName(base)) {
    const tokens = base.split("_");
    const legacySlots = { cn: creator_name, at: ad_type };
    tokens.forEach((t, i) => {
      const clean = sanitizeToken(legacySlots[t]);
      if (clean && i + 1 < tokens.length) tokens[i + 1] = clean;
    });
    base = tokens.join("_");
  } else if (base) {
    const tokens = base.split("_");
    const end = tokens.indexOf(AD_NAME_TERMINATOR);
    const body = end >= 0 ? tokens.slice(0, end) : tokens;
    const tail = end >= 0 ? tokens.slice(end + 1) : [];
    const idx = Object.fromEntries(AD_NAME_FIELDS.map(([key], i) => [key, i]));
    const fill = (key, val) => {
      const clean = sanitizeToken(val);
      if (clean && idx[key] < body.length) body[idx[key]] = clean;
    };
    fill("creator_name", creator_name);
    fill("editor_name", editor_name);
    base = [...body, AD_NAME_TERMINATOR, ...tail].join("_");
  }
  return hookNumber ? `${base}_hook${sanitizeToken(hookNumber)}` : base;
}
